import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import FotoUpload from '../components/FotoUpload';
import axiosInstance from '../utils/axios';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  Card,
  Space,
  Tooltip,
  Segmented,
  Tag,
  Select,
  Drawer,
  Timeline,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  FileTextOutlined,
  HomeOutlined,
  CarOutlined,
  EditOutlined,
  BankOutlined,
  HistoryOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin, canEdit } from '../utils/auth';

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalEditarVisible, setModalEditarVisible] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [form] = Form.useForm();
  const [formEditar] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tipoNuevo, setTipoNuevo] = useState('PERSONA');
  const [tipoEditar, setTipoEditar] = useState('PERSONA');
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [modalEstadoVisible, setModalEstadoVisible] = useState(false);
  const [clienteEstado, setClienteEstado] = useState(null);
  const [estadoNuevo, setEstadoNuevo] = useState('activo');
  const [motivoEstado, setMotivoEstado] = useState('');
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [drawerHistorialVisible, setDrawerHistorialVisible] = useState(false);
  const [historialEstados, setHistorialEstados] = useState([]);
  const [clienteHistorial, setClienteHistorial] = useState(null);

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/clientes');
      if (Array.isArray(response.data)) {
        setClientes(response.data);
      } else {
        message.error('Formato de respuesta incorrecto');
      }
    } catch (error) {
      message.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const abrirCambioEstado = (cliente) => {
    setClienteEstado(cliente);
    setEstadoNuevo(cliente.estado || 'activo');
    setMotivoEstado('');
    setModalEstadoVisible(true);
  };

  const confirmarCambioEstado = async () => {
    if (estadoNuevo !== 'activo' && !motivoEstado.trim()) {
      message.warning('El motivo es obligatorio');
      return;
    }
    setGuardandoEstado(true);
    try {
      await axiosInstance.put(`/clientes/${clienteEstado.dni}/estado`, { estado: estadoNuevo, motivo: motivoEstado.trim() });
      message.success('Estado actualizado');
      setModalEstadoVisible(false);
      setClientes(prev => prev.map(c => c.dni === clienteEstado.dni ? { ...c, estado: estadoNuevo } : c));
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al cambiar estado');
    } finally {
      setGuardandoEstado(false);
    }
  };

  const abrirHistorial = async (cliente) => {
    setClienteHistorial(cliente);
    setDrawerHistorialVisible(true);
    try {
      const res = await axiosInstance.get(`/clientes/${cliente.dni}/historial-estados`);
      setHistorialEstados(res.data);
    } catch { setHistorialEstados([]); }
  };

  const ESTADO_TAG = {
    activo:   { color: 'success',  label: 'Activo' },
    inactivo: { color: 'warning',  label: 'Inactivo' },
    baja:     { color: 'error',    label: 'Baja' },
  };

  const onFinish = async (values) => {
    try {
      console.log('Valores del formulario:', values);

      const dni = String(values.dni || '').trim();
      if (!/^\d{4,10}(-\d)?$/.test(dni)) {
        message.error(tipoNuevo === 'EMPRESA' ? 'El RUC debe tener solo dígitos, o dígitos-dígito verificador (ej. 80108372-9)' : 'La CI debe contener solo dígitos');
        return;
      }

      const clienteData = {
        ...values,
        dni,
        tipo_cliente: tipoNuevo,
        apellido: tipoNuevo === 'EMPRESA' ? (values.apellido || '') : values.apellido,
      };

      console.log('Datos a enviar:', clienteData);

      const response = await axiosInstance.post('/clientes', clienteData);
      console.log('Respuesta del servidor:', response.data);

      if (response.data.status === 'success') {
        message.success(response.data.message);
        setModalVisible(false);
        cargarClientes();
        form.resetFields();
      } else {
        message.error(response.data.message || 'Error al crear cliente');
      }
    } catch (error) {
      console.error('Error completo:', error);
      console.error('Response data:', error.response?.data);
      console.error('Request data:', error.config?.data);

      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Error al crear cliente';
      message.error(errorMessage);
    }
  };

  const abrirEditar = (cliente) => {
    setClienteEditando(cliente);
    const tipo = cliente.tipo_cliente || 'PERSONA';
    setTipoEditar(tipo);
    formEditar.setFieldsValue({
      tipo_cliente: tipo,
      nombre: cliente.nombre,
      apellido: cliente.apellido,
      celular: cliente.celular,
      email: cliente.email,
      ruc: cliente.ruc,
      domicilio: cliente.domicilio,
    });
    setModalEditarVisible(true);
  };

  const onFinishEditar = async (values) => {
    try {
      await axiosInstance.put(`/clientes/${clienteEditando.dni}`, { ...values, tipo_cliente: tipoEditar });
      message.success('Cliente actualizado');
      setModalEditarVisible(false);
      cargarClientes();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al actualizar cliente');
    }
  };

  const filteredClientes = clientes.filter(cliente => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      cliente.dni?.toString().toLowerCase().includes(searchLower) ||
      cliente.nombre?.toLowerCase().includes(searchLower) ||
      cliente.apellido?.toLowerCase().includes(searchLower) ||
      cliente.celular?.toLowerCase().includes(searchLower) ||
      cliente.email?.toLowerCase().includes(searchLower) ||
      cliente.ruc?.toLowerCase().includes(searchLower)
    );
  }).filter(c => filtroEstado === 'todos' || (c.estado || 'activo') === filtroEstado);

  const columns = [
    {
      title: 'CI / RUC',
      dataIndex: 'dni',
      key: 'dni',
      sorter: (a, b) => String(a.dni || '').localeCompare(String(b.dni || '')),
      render: (dni, record) => {
        const esEmpresa = record.tipo_cliente === 'EMPRESA';
        return (
          <Space direction="vertical" size={0}>
            <Tag
              icon={esEmpresa ? <BankOutlined /> : <IdcardOutlined />}
              color={esEmpresa ? 'purple' : 'blue'}
              style={{ fontSize: 10, marginBottom: 2 }}
            >
              {esEmpresa ? 'Empresa' : 'Persona'}
            </Tag>
            <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{dni}</span>
          </Space>
        );
      },
    },
    {
      title: 'Nombre / Razón Social',
      key: 'nombre_completo',
      sorter: (a, b) => (`${a.nombre} ${a.apellido || ''}`).localeCompare(`${b.nombre} ${b.apellido || ''}`),
      render: (_, record) => (
        <Space
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/clientes/${record.dni}`)}
        >
          {record.tipo_cliente === 'EMPRESA'
            ? <BankOutlined style={{ color: '#8e44ad' }} />
            : <UserOutlined style={{ color: hssColors.primary }} />}
          <span style={{ fontWeight: 600, color: hssColors.primary, textDecoration: 'underline' }}>
            {record.nombre}{record.apellido ? ` ${record.apellido}` : ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Teléfono',
      dataIndex: 'celular',
      key: 'celular',
      sorter: (a, b) => (a.celular || '').localeCompare(b.celular || ''),
      render: (celular) => celular ? (
        <Space>
          <PhoneOutlined style={{ color: hssColors.success }} />
          <span>{celular}</span>
        </Space>
      ) : '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
      render: (email) => email ? (
        <Space>
          <MailOutlined style={{ color: hssColors.info }} />
          <span>{email}</span>
        </Space>
      ) : '-',
    },
    {
      title: 'RUC',
      dataIndex: 'ruc',
      key: 'ruc',
      sorter: (a, b) => (a.ruc || '').localeCompare(b.ruc || ''),
      render: (ruc) => ruc || '-',
    },
    {
      title: 'Cédula',
      key: 'foto_cedula',
      width: 140,
      render: (_, c) => (
        <FotoUpload
          label="Foto cédula"
          urlEndpoint={`/clientes/${c.dni}/fotos`}
          deleteEndpoint={`/clientes/${c.dni}/fotos`}
          fotoActual={c.foto_cedula}
          onSuccess={(url) => setClientes(prev => prev.map(x => x.dni === c.dni ? { ...x, foto_cedula: url } : x))}
          onDelete={() => setClientes(prev => prev.map(x => x.dni === c.dni ? { ...x, foto_cedula: null } : x))}
        />
      ),
    },
    {
      title: 'Vehículos',
      key: 'total_vehiculos',
      sorter: (a, b) => (a.total_vehiculos || 0) - (b.total_vehiculos || 0),
      render: (_, c) => (
        <Space direction="vertical" size={0}>
          <Space>
            <CarOutlined style={{ color: hssColors.secondary }} />
            <span style={{ fontWeight: 700, color: hssColors.primary }}>{c.total_vehiculos || 0}</span>
          </Space>
          {(c.vehiculos_nacional > 0 || c.vehiculos_internacional > 0) && (
            <Space size={4}>
              {c.vehiculos_nacional > 0 && (
                <Tag color="geekblue" style={{ fontSize: 10, margin: 0, padding: '0 5px' }}>
                  Nac: {c.vehiculos_nacional}
                </Tag>
              )}
              {c.vehiculos_internacional > 0 && (
                <Tag color="purple" style={{ fontSize: 10, margin: 0, padding: '0 5px' }}>
                  Int: {c.vehiculos_internacional}
                </Tag>
              )}
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (estado) => {
        const e = ESTADO_TAG[estado || 'activo'] || ESTADO_TAG.activo;
        return <Tag color={e.color}>{e.label}</Tag>;
      },
    },
    ...(canEdit() ? [{
      title: '',
      key: 'acciones',
      width: 90,
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Editar cliente">
            <Button type="text" icon={<EditOutlined />} onClick={e => { e.stopPropagation(); abrirEditar(record); }} style={{ color: hssColors.primary }} />
          </Tooltip>
          <Tooltip title="Cambiar estado">
            <Button type="text" icon={<StopOutlined />} onClick={e => { e.stopPropagation(); abrirCambioEstado(record); }} style={{ color: hssColors.warning }} />
          </Tooltip>
          <Tooltip title="Historial de estados">
            <Button type="text" icon={<HistoryOutlined />} onClick={e => { e.stopPropagation(); abrirHistorial(record); }} style={{ color: hssColors.textSecondary }} />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: hssColors.backgroundPrimary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <UserOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Gestión de Clientes</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Administración de clientes del sistema</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nuevo Cliente
            </Button>
          </Space>
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total clientes', value: clientes.length, icon: <UserOutlined />, color: hssColors.primary },
            { label: 'Con vehículos', value: clientes.filter(c => c.total_vehiculos > 0).length, icon: <CarOutlined />, color: hssColors.success },
            { label: 'Sin vehículos', value: clientes.filter(c => !c.total_vehiculos || c.total_vehiculos === 0).length, icon: <UserOutlined />, color: hssColors.warning },
            { label: 'Empresas', value: clientes.filter(c => c.tipo === 'EMPRESA').length, icon: <BankOutlined />, color: hssColors.textSecondary },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Búsqueda */}
        <Card style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <Space style={{ width: '100%' }}>
            <Input
              placeholder="Buscar cliente por nombre, apellido, CI, teléfono, email, RUC..."
              prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
              style={{ flex: 1, borderRadius: '8px' }}
              size="large"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPaginaActual(1); }}
              allowClear
            />
            <Select
              value={filtroEstado}
              onChange={setFiltroEstado}
              size="large"
              style={{ width: 140 }}
              options={[
                { value: 'todos',    label: 'Todos' },
                { value: 'activo',   label: 'Activos' },
                { value: 'inactivo', label: 'Inactivos' },
                { value: 'baja',     label: 'Dados de baja' },
              ]}
            />
          </Space>
        </Card>

        {/* Tabla de clientes */}
        <Card
          style={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: commonStyles.shadowMd,
          }}
          styles={{
            body: { padding: 0 }
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredClientes}
            rowKey="dni"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: paginaActual,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (p, size) => { setPaginaActual(p); setPageSize(size); },
              showTotal: (total) => `Total: ${total} clientes`,
            }}
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          />
        </Card>
      </div>

      {/* Modal Nuevo Cliente */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '20px',
            fontWeight: '700',
            color: hssColors.primary,
          }}>
            <UserOutlined />
            <span>Nuevo Cliente</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: '24px' }}>

          {/* Selector de tipo */}
          <Form.Item label="Tipo de cliente" style={{ marginBottom: 20 }}>
            <Segmented
              block
              size="large"
              value={tipoNuevo}
              onChange={val => { setTipoNuevo(val); form.resetFields(['dni', 'apellido']); }}
              options={[
                { label: 'Persona Física', value: 'PERSONA', icon: <IdcardOutlined /> },
                { label: 'Empresa / Jurídico', value: 'EMPRESA', icon: <BankOutlined /> },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="dni"
            label={tipoNuevo === 'EMPRESA' ? 'RUC' : 'CI (Cédula de Identidad)'}
            rules={[
              { required: true, message: tipoNuevo === 'EMPRESA' ? 'Ingrese el RUC' : 'Ingrese la CI' },
              {
                pattern: /^\d{4,10}(-\d)?$/,
                message: tipoNuevo === 'EMPRESA'
                  ? 'El RUC debe tener dígitos, opcionalmente seguidos de un guion y el dígito verificador (ej. 80108372-9)'
                  : 'La CI debe contener solo dígitos',
              },
            ]}
          >
            <Input
              prefix={tipoNuevo === 'EMPRESA'
                ? <BankOutlined style={{ color: '#8e44ad' }} />
                : <IdcardOutlined style={{ color: hssColors.gray400 }} />}
              placeholder={tipoNuevo === 'EMPRESA' ? 'Ej: 80108372-9' : 'Ej: 1234567'}
              maxLength={12}
              size="large"
              style={{ borderRadius: '8px' }}
              onKeyPress={(e) => {
                const c = e.key;
                if (!/[0-9-]/.test(c)) e.preventDefault();
              }}
            />
          </Form.Item>

          <Form.Item
            name="nombre"
            label={tipoNuevo === 'EMPRESA' ? 'Razón Social' : 'Nombre'}
            rules={[{ required: true, message: tipoNuevo === 'EMPRESA' ? 'Ingrese la razón social' : 'Ingrese el nombre' }]}
          >
            <Input
              prefix={tipoNuevo === 'EMPRESA'
                ? <BankOutlined style={{ color: '#8e44ad' }} />
                : <UserOutlined style={{ color: hssColors.gray400 }} />}
              placeholder={tipoNuevo === 'EMPRESA' ? 'Nombre legal de la empresa' : 'Nombre del cliente'}
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          {tipoNuevo === 'PERSONA' && (
            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[{ required: true, message: 'Ingrese el apellido' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: hssColors.gray400 }} />}
                placeholder="Apellido del cliente"
                size="large"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          )}

          {tipoNuevo === 'EMPRESA' && (
            <Form.Item name="ruc" label="RUC completo (con dígito verificador)">
              <Input
                prefix={<FileTextOutlined style={{ color: hssColors.gray400 }} />}
                placeholder="Ej: 80012345-1"
                size="large"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          )}

          {tipoNuevo === 'PERSONA' && (
            <Form.Item name="ruc" label="RUC (si aplica)">
              <Input
                prefix={<FileTextOutlined style={{ color: hssColors.gray400 }} />}
                placeholder="RUC (opcional)"
                size="large"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          )}

          <Form.Item name="email" label="Email">
            <Input
              prefix={<MailOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="correo@ejemplo.com"
              type="email"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="celular" label="Celular / Teléfono">
            <Input
              prefix={<PhoneOutlined style={{ color: hssColors.gray400 }} />}
              placeholder="Número de contacto"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item name="domicilio" label="Domicilio">
            <Input
              placeholder="Calle, número, ciudad"
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setModalVisible(false)}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  boxShadow: commonStyles.shadowMd,
                }}
              >
                Guardar Cliente
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar Cliente */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 20, fontWeight: 700, color: hssColors.primary }}>
            <EditOutlined />
            <span>
              Editar {tipoEditar === 'EMPRESA' ? 'Empresa' : 'Cliente'} —{' '}
              {tipoEditar === 'EMPRESA' ? 'RUC' : 'CI'} {clienteEditando?.dni}
            </span>
          </div>
        }
        open={modalEditarVisible}
        onCancel={() => setModalEditarVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={formEditar} onFinish={onFinishEditar} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item label="Tipo de cliente" style={{ marginBottom: 20 }}>
            <Segmented
              block
              size="large"
              value={tipoEditar}
              onChange={val => setTipoEditar(val)}
              options={[
                { label: 'Persona Física', value: 'PERSONA', icon: <IdcardOutlined /> },
                { label: 'Empresa / Jurídico', value: 'EMPRESA', icon: <BankOutlined /> },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="nombre"
            label={tipoEditar === 'EMPRESA' ? 'Razón Social' : 'Nombre'}
            rules={[{ required: true }]}
          >
            <Input
              prefix={tipoEditar === 'EMPRESA'
                ? <BankOutlined style={{ color: '#8e44ad' }} />
                : <UserOutlined style={{ color: hssColors.gray400 }} />}
              size="large" style={{ borderRadius: 8 }}
            />
          </Form.Item>
          {tipoEditar === 'PERSONA' && (
            <Form.Item name="apellido" label="Apellido" rules={[{ required: true }]}>
              <Input prefix={<UserOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
            </Form.Item>
          )}
          <Form.Item name="celular" label="Celular / Teléfono">
            <Input prefix={<PhoneOutlined style={{ color: hssColors.gray400 }} />} placeholder="Número de contacto" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input prefix={<MailOutlined style={{ color: hssColors.gray400 }} />} placeholder="correo@ejemplo.com" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item
            name="ruc"
            label={tipoEditar === 'EMPRESA' ? 'RUC completo (con dígito verificador)' : 'RUC (si aplica)'}
          >
            <Input
              prefix={<FileTextOutlined style={{ color: hssColors.gray400 }} />}
              placeholder={tipoEditar === 'EMPRESA' ? 'Ej: 80012345-1' : 'RUC (opcional)'}
              size="large" style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item name="domicilio" label="Domicilio">
            <Input prefix={<HomeOutlined style={{ color: hssColors.gray400 }} />} placeholder="Calle, número, ciudad" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalEditarVisible(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                Guardar cambios
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal cambio de estado */}
      <Modal
        title={<Space><StopOutlined />Cambiar estado del cliente</Space>}
        open={modalEstadoVisible}
        onCancel={() => setModalEstadoVisible(false)}
        onOk={confirmarCambioEstado}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={guardandoEstado}
        okButtonProps={{ disabled: estadoNuevo !== 'activo' && !motivoEstado.trim() }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>Cliente: {clienteEstado?.nombre} {clienteEstado?.apellido || ''}</div>
            <div style={{ marginBottom: 6, color: hssColors.textSecondary, fontSize: 12 }}>Estado actual: <Tag color={ESTADO_TAG[clienteEstado?.estado || 'activo']?.color}>{ESTADO_TAG[clienteEstado?.estado || 'activo']?.label}</Tag></div>
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Nuevo estado</div>
            <Select
              value={estadoNuevo}
              onChange={setEstadoNuevo}
              style={{ width: '100%' }}
              size="large"
              options={[
                { value: 'activo',   label: 'Activo' },
                { value: 'inactivo', label: 'Inactivo' },
                { value: 'baja',     label: 'Dado de baja' },
              ]}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              Motivo {estadoNuevo !== 'activo' ? <span style={{ color: 'red' }}>*</span> : '(opcional)'}
            </div>
            <Input.TextArea
              value={motivoEstado}
              onChange={e => setMotivoEstado(e.target.value)}
              placeholder={estadoNuevo === 'baja' ? 'Ej: Cliente solicitó cancelar el servicio por...' : 'Motivo del cambio de estado'}
              rows={3}
            />
          </div>
        </Space>
      </Modal>

      {/* Drawer historial de estados */}
      <Drawer
        title={<Space><HistoryOutlined />Historial de estados — {clienteHistorial?.nombre} {clienteHistorial?.apellido || ''}</Space>}
        open={drawerHistorialVisible}
        onClose={() => setDrawerHistorialVisible(false)}
        width={420}
      >
        {historialEstados.length === 0
          ? <div style={{ color: hssColors.textSecondary, textAlign: 'center', paddingTop: 40 }}>Sin cambios de estado registrados</div>
          : <Timeline
              items={historialEstados.map(h => ({
                color: ESTADO_TAG[h.estado_nuevo]?.color === 'success' ? 'green' : ESTADO_TAG[h.estado_nuevo]?.color === 'warning' ? 'orange' : 'red',
                children: (
                  <div>
                    <Space style={{ marginBottom: 2 }}>
                      <Tag color={ESTADO_TAG[h.estado_anterior]?.color}>{ESTADO_TAG[h.estado_anterior]?.label || h.estado_anterior}</Tag>
                      →
                      <Tag color={ESTADO_TAG[h.estado_nuevo]?.color}>{ESTADO_TAG[h.estado_nuevo]?.label || h.estado_nuevo}</Tag>
                    </Space>
                    {h.motivo && <div style={{ fontStyle: 'italic', color: hssColors.textSecondary, fontSize: 13 }}>"{h.motivo}"</div>}
                    <div style={{ fontSize: 11, color: hssColors.gray400, marginTop: 2 }}>
                      {h.usuario_nombre} · {h.fecha ? new Date(h.fecha).toLocaleString('es-PY') : ''}
                    </div>
                  </div>
                ),
              }))}
            />
        }
      </Drawer>

      <Footer />
    </div>
  );
};

export default Clientes;